/**
 * One menu component for every menu in Nova: the menu bar's drop-downs, every
 * ⋯ button, and right-click menus.
 *
 * Standard desktop behaviour, so nothing has to be learned:
 * - opens below its anchor (or at the pointer), and flips or shifts to stay on
 *   screen at any window size;
 * - arrow keys move, Enter or Space runs, Right opens a sub-menu and Left closes
 *   it, Escape closes one level, typing a letter jumps to the next item starting
 *   with it;
 * - a click outside, a scroll of the page or a window resize closes it;
 * - groups are separated by a rule; disabled items say why on hover.
 */

import { useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { allowedItems, grouped, type MenuItem } from "../core/registry";
import { useSession } from "../core/session";
import { Icon } from "./Icons";

export type Anchor = { rect: DOMRect; align?: "start" | "end" } | { x: number; y: number };

const MARGIN = 8;

function position(anchor: Anchor, w: number, h: number, sub = false): { left: number; top: number } {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let left: number;
  let top: number;
  if ("rect" in anchor) {
    const r = anchor.rect;
    if (sub) {
      left = r.right - 2;
      top = r.top - 5;
      if (left + w > vw - MARGIN) left = r.left - w + 2;
    } else {
      left = anchor.align === "end" ? r.right - w : r.left;
      top = r.bottom + 4;
      if (top + h > vh - MARGIN && r.top - h - 4 > MARGIN) top = r.top - h - 4;
    }
  } else {
    left = anchor.x;
    top = anchor.y;
    if (left + w > vw - MARGIN) left = anchor.x - w;
    if (top + h > vh - MARGIN) top = anchor.y - h;
  }
  left = Math.max(MARGIN, Math.min(left, vw - w - MARGIN));
  top = Math.max(MARGIN, Math.min(top, vh - h - MARGIN));
  return { left, top };
}

export function PopupMenu({
  items: given,
  anchor,
  onClose,
  header,
  sub = false,
  label,
  onPicked,
}: {
  items: MenuItem[];
  anchor: Anchor;
  onClose: () => void;
  header?: ReactNode;
  sub?: boolean;
  label?: string;
  /** A submenu's: an item was chosen, so the whole menu closes, not just this level. */
  onPicked?: () => void;
}) {
  // Only what the signed-in role can do (every item with accounts off).
  const session = useSession();
  const items = useMemo(() => allowedItems(given, session), [given, session]);
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const [focus, setFocus] = useState(-1);
  const [openSub, setOpenSub] = useState<{ index: number; rect: DOMRect } | null>(null);
  const flat = items;

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos(position(anchor, r.width, Math.min(r.height, window.innerHeight - 2 * MARGIN), sub));
  }, [anchor, sub, items.length]);

  useEffect(() => {
    if (!sub) ref.current?.focus();
    if (sub) setFocus(0);
  }, [sub]);

  useEffect(() => {
    if (sub) return;
    const close = (e: Event) => {
      const target = e.target as Node | null;
      if (target && document.querySelectorAll(".nv-menu").length) {
        for (const menu of document.querySelectorAll(".nv-menu")) if (menu.contains(target)) return;
      }
      onClose();
    };
    const onResize = () => onClose();
    const t = window.setTimeout(() => {
      document.addEventListener("pointerdown", close, true);
      window.addEventListener("resize", onResize);
      window.addEventListener("blur", onResize);
    }, 0);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener("pointerdown", close, true);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("blur", onResize);
    };
  }, [onClose, sub]);

  const enabled = (i: number) => !flat[i]?.disabled;
  const move = (dir: 1 | -1) => {
    if (!flat.length) return;
    let i = focus;
    for (let n = 0; n < flat.length; n++) {
      i = (i + dir + flat.length) % flat.length;
      if (enabled(i)) break;
    }
    setFocus(i);
  };

  const activate = (i: number, el?: HTMLElement | null) => {
    const item = flat[i];
    if (!item || item.disabled) return;
    if (item.submenu?.length) {
      const rect = (el ?? ref.current?.querySelector<HTMLElement>(`[data-index="${i}"]`))?.getBoundingClientRect();
      if (rect) setOpenSub({ index: i, rect });
      return;
    }
    (onPicked ?? onClose)();
    void item.run?.();
  };

  const onKey = (e: KeyboardEvent) => {
    if (openSub) return;
    if (e.key === "ArrowDown") move(1);
    else if (e.key === "ArrowUp") move(-1);
    else if (e.key === "Home") setFocus(flat.findIndex((_, i) => enabled(i)));
    else if (e.key === "End") setFocus(flat.length - 1 - [...flat].reverse().findIndex((_, i) => enabled(flat.length - 1 - i)));
    else if (e.key === "Enter" || e.key === " ") activate(focus);
    else if (e.key === "ArrowRight" && flat[focus]?.submenu) activate(focus);
    else if (e.key === "Escape" || (e.key === "ArrowLeft" && sub)) onClose();
    else if (e.key.length === 1 && /\S/.test(e.key)) {
      const ch = e.key.toLowerCase();
      for (let n = 1; n <= flat.length; n++) {
        const i = (focus + n) % flat.length;
        if (enabled(i) && flat[i].label.toLowerCase().startsWith(ch)) {
          setFocus(i);
          break;
        }
      }
    } else return;
    e.preventDefault();
    e.stopPropagation();
  };

  useEffect(() => {
    if (focus >= 0) ref.current?.querySelector<HTMLElement>(`[data-index="${focus}"]`)?.scrollIntoView({ block: "nearest" });
  }, [focus]);

  let index = -1;
  const body = (
    <div
      ref={ref}
      className={`nv-menu ${sub ? "sub" : ""}`}
      role="menu"
      aria-label={label}
      tabIndex={-1}
      onKeyDown={onKey}
      style={{ left: pos?.left ?? -9999, top: pos?.top ?? -9999, maxHeight: `calc(100vh - ${2 * MARGIN}px)` }}
      onContextMenu={(e) => e.preventDefault()}
    >
      {header && <div className="nv-menu-head">{header}</div>}
      {grouped(items).map((group, g) => (
        <div key={g} className="nv-menu-group" role="group">
          {g > 0 && <hr />}
          {group.map((item) => {
            index += 1;
            const i = index;
            return (
              <button
                key={item.id}
                data-index={i}
                role={item.checked !== undefined ? "menuitemcheckbox" : "menuitem"}
                aria-checked={item.checked}
                aria-disabled={item.disabled || undefined}
                aria-haspopup={item.submenu ? "menu" : undefined}
                aria-expanded={item.submenu ? openSub?.index === i : undefined}
                className={`nv-menu-item ${focus === i ? "hl" : ""} ${item.danger ? "danger" : ""} ${item.disabled ? "off" : ""}`}
                title={item.hint}
                onMouseEnter={(e) => {
                  setFocus(i);
                  if (item.submenu && !item.disabled) setOpenSub({ index: i, rect: e.currentTarget.getBoundingClientRect() });
                  else setOpenSub(null);
                }}
                onClick={(e) => activate(i, e.currentTarget)}
              >
                <span className="nv-menu-icon">
                  {item.checked !== undefined ? (item.checked ? <Icon name="check" size={15} /> : null) : item.icon ? <Icon name={item.icon} size={15} /> : null}
                </span>
                <span className="nv-menu-label">{item.label}</span>
                {item.shortcut && <kbd>{item.shortcut}</kbd>}
                {item.submenu && <Icon name="chevron" size={13} className="nv-menu-chev" />}
              </button>
            );
          })}
        </div>
      ))}
      {openSub && flat[openSub.index]?.submenu && (
        <PopupMenu
          sub
          items={flat[openSub.index].submenu!}
          anchor={{ rect: openSub.rect }}
          onClose={() => {
            setOpenSub(null);
            ref.current?.focus();
          }}
          onPicked={onPicked ?? onClose}
        />
      )}
    </div>
  );
  return sub ? body : createPortal(body, document.body);
}

/** A ⋯ button that opens a menu. */
export function MoreButton({
  items,
  label = "More options",
  className = "",
  size = 16,
  align = "end",
}: {
  items: MenuItem[] | (() => MenuItem[]);
  label?: string;
  className?: string;
  size?: number;
  align?: "start" | "end";
}) {
  const [open, setOpen] = useState<DOMRect | null>(null);
  const resolved = open ? (typeof items === "function" ? items() : items) : [];
  return (
    <>
      <button
        className={`nv-iconbtn ${open ? "on" : ""} ${className}`}
        aria-label={label}
        title={label}
        aria-haspopup="menu"
        aria-expanded={!!open}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(open ? null : e.currentTarget.getBoundingClientRect());
        }}
      >
        <Icon name="more" size={size} />
      </button>
      {open && <PopupMenu items={resolved} anchor={{ rect: open, align }} onClose={() => setOpen(null)} label={label} />}
    </>
  );
}
